// Drawola Studio: per-browser UI preferences (grid/list, sort order) and
// the returning-user flag.
//
// Fork-only. Stored under `drawola-studio-*` localStorage keys, always behind
// try/catch: Safari private mode and storage-blocked embeds throw on access.

export type StudioLayout = "grid" | "list";
export type StudioSort = "updated" | "name" | "created";

const LAYOUT_KEY = "drawola-studio-layout";
const SORT_KEY = "drawola-studio-sort";
/**
 * Set to "1" while the library holds the visitor's own work or Drive is signed
 * in. A derived hint only: the welcome follows the separate acknowledgment
 * cookie in `welcomeAcknowledgment.ts`, and nothing reads this key to decide it.
 */
export const HAS_DRAWS_KEY = "drawola-studio-has-draws";

const LAYOUTS: readonly StudioLayout[] = ["grid", "list"];
const SORTS: readonly StudioSort[] = ["updated", "name", "created"];

const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // preference is a nicety — never break the Studio over it
  }
};

export const loadLayout = (): StudioLayout => {
  const stored = read(LAYOUT_KEY) as StudioLayout | null;
  return stored && LAYOUTS.includes(stored) ? stored : "grid";
};

export const saveLayout = (layout: StudioLayout): void =>
  write(LAYOUT_KEY, layout);

export const loadSort = (): StudioSort => {
  const stored = read(SORT_KEY) as StudioSort | null;
  return stored && SORTS.includes(stored) ? stored : "updated";
};

export const saveSort = (sort: StudioSort): void => write(SORT_KEY, sort);

/** Records whether this browser holds the visitor's own work; a hint only. */
export const saveHasDraws = (hasDraws: boolean): void =>
  write(HAS_DRAWS_KEY, hasDraws ? "1" : "0");
